import { useCallback, useRef } from "react";

export interface ScrollManager {
  messageHistoryRef: React.RefObject<HTMLDivElement>;
  scrollToBottom: () => void;
  scrollToBottomIfAtBottom: () => void;
  isAtBottom: () => boolean;
  scrollUserMessageToTop: () => void;
  handleScroll: () => void;
  checkScrollPosition: () => void;
  debouncedScrollCheck: () => void;
}

export const useScrollManager = (
  setShowScrollToBottom: (show: boolean) => void
): ScrollManager => {
  const messageHistoryRef = useRef<HTMLDivElement>(null);
  const scrollCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check if user is at or near the bottom of the scroll container
  const isAtBottom = useCallback((): boolean => {
    if (!messageHistoryRef.current) return true;
    const { scrollTop, scrollHeight, clientHeight } = messageHistoryRef.current;
    // Consider "at bottom" if within 100px of the bottom
    return scrollTop + clientHeight >= scrollHeight - 100;
  }, []);

  const scrollToBottom = useCallback(() => {
    if (messageHistoryRef.current) {
      const { scrollHeight, clientHeight } = messageHistoryRef.current;
      messageHistoryRef.current.scrollTop = scrollHeight - clientHeight;
    }
  }, []);

  // Only scroll to bottom if user is already at/near the bottom
  // This provides natural scroll behavior during streaming
  const scrollToBottomIfAtBottom = useCallback(() => {
    if (isAtBottom()) {
      scrollToBottom();
    }
  }, [isAtBottom, scrollToBottom]);

  const scrollUserMessageToTop = useCallback(() => {
    const container = messageHistoryRef.current;
    if (!container) return;
    const userMessages = container.querySelectorAll(".user-message");
    const lastUserMessage = userMessages[userMessages.length - 1] as HTMLElement;
    if (lastUserMessage) {
      // Calculate offset relative to the scroll container and scroll directly.
      // This is more reliable than scrollIntoView which can be affected by
      // nested scroll containers or padding.
      container.scrollTop = lastUserMessage.offsetTop - container.offsetTop;
    }
  }, []);

  const handleScroll = useCallback(() => {
    if (messageHistoryRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        messageHistoryRef.current;
      const paddingBottom = clientHeight * 0.4;
      const actualContentHeight = scrollHeight - paddingBottom;
      const isAtBottom = scrollTop + clientHeight >= actualContentHeight - 10;
      setShowScrollToBottom(!isAtBottom);
    }
  }, [setShowScrollToBottom]);

  const checkScrollPosition = useCallback(() => {
    if (messageHistoryRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        messageHistoryRef.current;
      const paddingBottom = clientHeight * 0.4;
      const actualContentHeight = scrollHeight - paddingBottom;
      const isAtBottom = scrollTop + clientHeight >= actualContentHeight - 10;
      setShowScrollToBottom(!isAtBottom);
    }
  }, [setShowScrollToBottom]);

  const debouncedScrollCheck = useCallback(() => {
    if (scrollCheckTimeoutRef.current) {
      clearTimeout(scrollCheckTimeoutRef.current);
    }
    scrollCheckTimeoutRef.current = setTimeout(() => {
      checkScrollPosition();
    }, 200);
  }, [checkScrollPosition]);

  return {
    messageHistoryRef,
    scrollToBottom,
    scrollToBottomIfAtBottom,
    isAtBottom,
    scrollUserMessageToTop,
    handleScroll,
    checkScrollPosition,
    debouncedScrollCheck,
  };
};
